import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { API_CONFIG, API_ENDPOINTS } from '@/config/api';
import { ADMIN_ROUTES } from '@/constants/routes';
import { toast } from '@/lib/toast';
import type { ApiErrorPayload } from '@/types/api';

/**
 * Shared Axios instance.
 * Credentials (cookies) are sent automatically via withCredentials.
 * The access token itself is never attached from JS — HttpOnly cookie handles it.
 */
export const apiClient = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
  withCredentials: true,
});

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Inject access token from Zustand store via dynamic import to avoid circular dependencies
    if (typeof window !== 'undefined') {
      try {
        const { useAuthStore } = await import('@/store/auth.store');
        const token = useAuthStore.getState().accessToken;
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // Ignore
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

/**
 * Public auth endpoints: a 401/4xx here means bad credentials/token in the payload,
 * not an expired session. Never refresh or redirect — surface the error via toast.
 */
const PUBLIC_AUTH_ENDPOINTS: readonly string[] = [
  API_ENDPOINTS.auth.login,
  API_ENDPOINTS.auth.refresh,
  API_ENDPOINTS.auth.forgotPassword,
  API_ENDPOINTS.auth.resetPassword,
];

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => {
    const isGet = response.config.method?.toLowerCase() === 'get';

    if (typeof window !== 'undefined' && !response.config.skipSuccessToast && !isGet) {
      const message = extractSuccessMessage(response.data);
      if (message) {
        toast.success(message);
      }
    }
    return response;
  },
  async (error: AxiosError<ApiErrorPayload>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const skipToast = originalRequest?.skipErrorToast;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Avoid intercepting the refresh endpoint itself
      if (!PUBLIC_AUTH_ENDPOINTS.includes(originalRequest.url ?? '')) {
        if (isRefreshing) {
          try {
            await new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });
            // Retry the request; the request interceptor will pick up the new token
            return apiClient(originalRequest);
          } catch (err) {
            return Promise.reject(err);
          }
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const { useAuthStore } = await import('@/store/auth.store');
          const store = useAuthStore.getState();
          const refreshToken = store.refreshToken;

          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          // Attempt to refresh token directly against the backend
          const response = await axios.post(
            API_CONFIG.baseURL + API_ENDPOINTS.auth.refresh,
            { refreshToken },
            { headers: { 'Content-Type': 'application/json' } },
          );

          // Assuming the backend returns { data: { accessToken, refreshToken } }
          const newTokens = response.data?.data;
          if (newTokens?.accessToken) {
            store.setTokens(newTokens.accessToken, newTokens.refreshToken || refreshToken);

            if (typeof document !== 'undefined') {
              document.cookie = `dc_admin_access_token=${newTokens.accessToken}; path=/; max-age=86400; SameSite=Lax`;
            }

            processQueue(null);
            return apiClient(originalRequest);
          } else {
            throw new Error('Invalid refresh response');
          }
        } catch (refreshError) {
          processQueue(refreshError);

          if (typeof window !== 'undefined') {
            document.cookie = 'dc_admin_access_token=; path=/; max-age=0; SameSite=Lax';
            const { useAuthStore } = await import('@/store/auth.store');
            useAuthStore.getState().logout();

            if (window.location.pathname !== ADMIN_ROUTES.login) {
              window.location.assign(ADMIN_ROUTES.login);
            }
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    }

    if (typeof window !== 'undefined' && !skipToast) {
      toast.error(getApiErrorMessage(error));
    }

    return Promise.reject(error);
  },
);

function extractSuccessMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  if (!('message' in data)) return null;
  const message = (data as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() ? message : null;
}

export function getApiErrorMessage(error: unknown, fallback = 'Request failed'): string {
  if (axios.isAxiosError<ApiErrorPayload>(error)) {
    const data = error.response?.data;
    if (data?.errors) {
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        const firstError = data.errors[0];
        if (typeof firstError === 'string') {
          const colonIndex = firstError.indexOf(': ');
          return colonIndex !== -1 ? firstError.substring(colonIndex + 2) : firstError;
        }
        if (typeof firstError === 'object' && firstError !== null) {
          const msg = (firstError as { message?: string }).message || Object.values(firstError)[0];
          if (typeof msg === 'string') return msg;
        }
      } else if (typeof data.errors === 'object' && data.errors !== null) {
        const firstVal = Object.values(data.errors)[0];
        if (Array.isArray(firstVal) && typeof firstVal[0] === 'string') {
          return firstVal[0];
        }
        if (typeof firstVal === 'string') return firstVal;
      }
    }

    if (Array.isArray(data?.message)) {
      return data.message.join(', ');
    }

    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message;
    }

    if (typeof (data as unknown as { error?: unknown })?.error === 'string') {
      const errStr = (data as unknown as { error?: string }).error;
      if (errStr?.trim()) return errStr;
    }

    return error.message || fallback;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}
